import { IColumnDefinition } from '@common/types';
import { Box, Button, FormControl, Typography } from '@mui/material';
import { theme } from '@store';
import { Select } from '@ui';
import { t } from 'i18next';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTasks } from './hooks';
import { AddTask, WorklistItem } from '@components';
import { TaskTable } from './TaskTable';
import { FactCheck } from '@mui/icons-material';

const columns: IColumnDefinition[] = [
  {
    id: 'task',
    label: 'Task',
    sortable: true,
  },
  {
    id: 'priority',
    label: 'Priority',
    sortable: true,
    align: 'center',
  },
  {
    id: 'due_date',
    label: 'Due date',
    sortable: true,
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    align: 'center',
  },
  {
    id: 'created_date',
    label: 'Created date',
    sortable: true,
  },
  {
    id: 'last_updated_at',
    label: 'Last updated',
    sortable: true,
  },
];

interface ITasksProps {
  patient_id: string;
  tabPanelInstrumentHeight: number;
  isEditDisabled: boolean;
}

export const Tasks = ({
  patient_id,
  tabPanelInstrumentHeight,
  isEditDisabled,
}: ITasksProps) => {
  const {
    filters,
    handleFilterChange,
    selectedTaskId,
    handleCloseDrawer,
    handleOpenTaskDetails,
    entries,
    isLoading,
    totalCount,
    handleChangeFilterProgram,
    handleChangeFilterStatus,
    handleChangeFilterPriority,
    programOptions,
    statusOptions,
    priorityOptions,
    handleResetFilter,
    isResetDisabled,
    isInitialFilters,
  } = useTasks({ patient_id });

  return (
    <>
      {isInitialFilters && entries.length === 0 && !isLoading ? (
        <Box
          sx={{
            height: 'calc(100vh - 310px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <FactCheck sx={{ width: 56, height: 56, mb: 2 }} color='primary' />
          <Typography variant='h6'>
            {t('patient.tasks.table.noResults.title')}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
            {t('patient.tasks.table.noResults.subTitle')}
          </Typography>
          <AddTask
            disabled={isEditDisabled}
            color='primary'
            variant='outlined'
            buttonText='ADD'
            sx={{ color: 'primary' }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: theme.palette.common.white,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              padding: 2,
              justifyContent: 'space-between',
              alignItems: 'center',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderBottom: 'none',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
              }}
            >
              <Select
                sx={{ maxWidth: '350px', minWidth: '20%' }}
                name='tasks-program'
                size='medium'
                label={t('patient.tasks.filters.program')}
                value={filters.programs[0]}
                onChange={handleChangeFilterProgram}
                options={programOptions}
                clearable
                clearLabel='All'
              />
              <Select
                sx={{ maxWidth: '350px', minWidth: '20%' }}
                name='tasks-priority'
                size='medium'
                label={t('patient.tasks.filters.priority')}
                value={filters.priorities[0]}
                onChange={handleChangeFilterPriority}
                options={priorityOptions}
                clearable
                clearLabel='All'
              />

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <FormControl sx={{ maxWidth: '350px', minWidth: '20%' }}>
                  <DatePicker
                    label={t('patient.tasks.filters.dueDate')}
                    name={'tasks-due-date'}
                    value={filters.due_date ? dayjs(filters.due_date) : null}
                    onChange={date => {
                      const formattedValue = date
                        ? dayjs(date).format('MMM D, YYYY')
                        : '';
                      handleFilterChange({ due_date: formattedValue });
                    }}
                    slotProps={{
                      field: {
                        clearable: true,
                      },
                    }}
                  />
                </FormControl>
              </LocalizationProvider>

              <Select
                sx={{ maxWidth: '350px', minWidth: '20%' }}
                name='care-notes-provider'
                size='medium'
                label={t('patient.tasks.filters.status')}
                value={filters.statuses[0]}
                onChange={handleChangeFilterStatus}
                options={statusOptions}
                clearable
                clearLabel='All'
              />
              <Button onClick={handleResetFilter} disabled={isResetDisabled}>
                {t('patient.tasks.filters.reset')}
              </Button>
            </Box>
          </Box>
          <TaskTable
            tabPanelInstrumentHeight={tabPanelInstrumentHeight}
            rows={entries}
            filters={filters}
            setFilters={handleFilterChange}
            columns={columns}
            totalCount={totalCount}
            loading={isLoading}
            patient_id={patient_id}
            onSelectTask={handleOpenTaskDetails}
            selectedTaskId={selectedTaskId}
          />
          {selectedTaskId && (
            <WorklistItem
              open={!!selectedTaskId}
              itemId={selectedTaskId}
              handleClose={handleCloseDrawer}
            />
          )}
        </Box>
      )}
    </>
  );
};
